import Alert from 'components/atoms/Alert/Alert';
import Button from 'components/atoms/Button/Button';
import Card from 'components/atoms/Card/Card';
import ContentButton from 'components/atoms/ContentButton/ContentButton';
import Heading from 'components/atoms/Heading/Heading';
import Overlay from 'components/atoms/Overlay/Overlay';
import Txt from 'components/atoms/Txt/Txt';
import ModalClose from 'components/assets/ModalClose.svg';
import { ReactNode, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { CancelAccount_walletBalance$key } from './__generated__/CancelAccount_walletBalance.graphql';
import classes from './CancelAccount.module.scss';
import { useCloseAccount } from './useCloseAccount';

export type Props = {
  walletBalanceRef: CancelAccount_walletBalance$key;
  children?: ReactNode;
};

const walletBalanceFragment = graphql`
  fragment CancelAccount_walletBalance on WalletBalance {
    amount {
      formatted
    }
  }
`;

export default function CancelAccount({ walletBalanceRef }: Props) {
  useFragment(walletBalanceFragment, walletBalanceRef);
  const { closeAccount, closeAccountIsInFlight, error, setError } = useCloseAccount();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);

  return (
    <div>
      {confirmDialogOpen && (
        <Overlay>
          <Card className={classes.root}>
            <div className={classes.closeButton}>
              <Button buttonType="tertiary" onClick={() => setConfirmDialogOpen(false)}>
                <ModalClose width={17} height={16} />
              </Button>
            </div>

            <Heading level={2} className={classes.cancelHeader}>
              Cancel Membership
            </Heading>
            <Txt component="p">
              We&apos;re sorry you&apos;re considering leaving us. By cancelling your membership,
              you will lose all the rewards you have earned as well as your membership history
              permanently.
            </Txt>

            <Txt component="p">
              To comply with financial regulations, we will retain a unique code for any PayPal
              accounts you may have linked to your Mobiworkx account. If you close your account now,
              you will not be able to link the same PayPal account to any future Mobiworkx account.
            </Txt>

            <Button
              buttonType="primary"
              buttonSize="large"
              disabled={closeAccountIsInFlight}
              onClick={closeAccount}
              className={classes.cancelAccountButton}
            >
              <Txt>Close my account</Txt>
            </Button>

            {error && (
              <Alert
                title={'An error occurred'}
                subtitle={error}
                type={'error'}
                onClose={() => {
                  setError(undefined);
                }}
                className={classes.cancelAccountErrorAlert}
              />
            )}
          </Card>
        </Overlay>
      )}
      <ContentButton onClick={() => setConfirmDialogOpen(true)}>
        <Txt className={classes.intro}>
          Once you cancel your membership, all your rewards and account history will be permanently
          forfeited.
        </Txt>
      </ContentButton>
    </div>
  );
}
